import type { LandingPageQuestion } from '@/types';

type QuestionValue = string | string[] | boolean | number | '';

interface QuestionRendererProps {
  question: LandingPageQuestion;
  value: QuestionValue;
  onChange: (value: string | string[] | boolean | number) => void;
  error?: string;
}

const inputClassName =
  'border border-gray-300 rounded-lg px-3 py-2 w-full text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';

function valueAsString(value: QuestionValue) {
  if (Array.isArray(value)) return '';
  return value === true || value === false ? String(value) : String(value ?? '');
}

export default function QuestionRenderer({ question, value, onChange, error }: QuestionRendererProps) {
  const selectedValues = Array.isArray(value) ? value : [];

  const toggleMultiSelect = (option: string) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter((selected) => selected !== option));
      return;
    }
    onChange([...selectedValues, option]);
  };

  return (
    <div className="bg-white">
      <label htmlFor={question.id} className="block text-sm font-medium text-gray-700 mb-1">
        {question.label}
        {question.required && <span className="text-red-500"> *</span>}
      </label>
      {question.helper && <p className="text-xs text-gray-500 mt-1 mb-2">{question.helper}</p>}

      {question.type === 'short_text' && (
        <input
          id={question.id}
          type="text"
          value={valueAsString(value)}
          onChange={(event) => onChange(event.target.value)}
          className={inputClassName}
        />
      )}

      {question.type === 'long_text' && (
        <textarea
          id={question.id}
          rows={4}
          value={valueAsString(value)}
          onChange={(event) => onChange(event.target.value)}
          className={inputClassName}
        />
      )}

      {question.type === 'email' && (
        <input
          id={question.id}
          type="email"
          value={valueAsString(value)}
          onChange={(event) => onChange(event.target.value)}
          className={inputClassName}
        />
      )}

      {question.type === 'phone' && (
        <input
          id={question.id}
          type="tel"
          value={valueAsString(value)}
          onChange={(event) => onChange(event.target.value)}
          className={inputClassName}
        />
      )}

      {question.type === 'number' && (
        <input
          id={question.id}
          type="number"
          value={valueAsString(value)}
          onChange={(event) =>
            onChange(event.target.value === '' ? '' : event.target.valueAsNumber)
          }
          className={inputClassName}
        />
      )}

      {question.type === 'select' && (
        <select
          id={question.id}
          value={valueAsString(value)}
          onChange={(event) => onChange(event.target.value)}
          className={inputClassName}
        >
          <option value="">Select an option</option>
          {(question.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}

      {question.type === 'multi_select' && (
        <div className="space-y-2">
          {(question.options ?? []).map((option) => (
            <label key={option} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => toggleMultiSelect(option)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              {option}
            </label>
          ))}
        </div>
      )}

      {question.type === 'yes_no' && (
        <div className="flex gap-4">
          {['yes', 'no'].map((option) => (
            <label key={option} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name={question.id}
                value={option}
                checked={value === option}
                onChange={() => onChange(option)}
                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              {option === 'yes' ? 'Yes' : 'No'}
            </label>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
